import React, { useEffect, useState, useMemo } from "react";
import {
  Card,
  Table,
  Tabs,
  Button,
  Input,
  Typography,
  Alert,
  Spin,
  message,
  Progress,
  Checkbox,
  Select,
  Tag,
  Space,
} from "antd";
import { CheckCircleOutlined, CloseCircleOutlined } from "@ant-design/icons";
import axiosInstance from "../../../../utils/axiosInstance";
import { LOCALE_LIST } from "../../../../constants/LocaleList";
import { DMA_LIST } from "../../../../constants/DMAList";
import { useNavigate } from "react-router-dom";

const { Title, Text } = Typography;
const { TabPane } = Tabs;

interface ResultStepProps {
  jobConfigId: string;
  onPrevious: () => void;
}

export interface ResultStepData {
  success: boolean;
}

const ResultStep: React.FC<ResultStepProps> = ({ jobConfigId, onPrevious }) => {
  const navigate = useNavigate();
  const [bidsColumns, setBidsColumns] = useState<any[]>([]);
  const [bidsRecords, setBidsRecords] = useState<any[]>([]);
  const [bidsJson, setBidsJson] = useState<any>();
  const [jobConfigData, setJobConfigData] = useState<any>();
  const [isAlert, setAlert] = useState(false);
  const [activeTab, setActiveTab] = useState<string>("facebook");
  const [facebookLoading, setFacebookLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [tableKey, setTableKey] = useState(0);
  const [filterState, setFilterState] = useState<Record<string, string[] | null>>({});
  const [selectedFilters, setSelectedFilters] = useState<
    {
      column: string;
      value: string;
      key: string;
    }[]
  >([]);
  const [formData, setFormData] = useState({
    controlAdsetId: "",
    controlBudget: "",
    testAdsetId: "",
    testBudget: "",
    googleControlCampaignId: "",
    googleControlBudget: "",
    googleTestCampaignId: "",
    googleTestBudget: "",
  });

  // Inject controlled filteredValue so removing a tag also clears the column's
  // filter checkmarks inside the Ant Design Table dropdown.
  const columnsWithFilters = useMemo(
    () =>
      bidsColumns.map((col: any) => ({
        ...col,
        filteredValue: filterState[col.key] ?? null,
      })),
    [bidsColumns, filterState]
  );

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        console.log("Fetching data for jobConfigId:", jobConfigId);

        // Fetch bid multiplier output data — may not be ready yet if the job
        // is still running (user clicked Skip Waiting on the previous step).
        let outputJson;
        let outputUnavailable = false;
        try {
          const outputResponse = await axiosInstance.get(
            `/api/bid-multiplier/output/${jobConfigId}`
          );
          outputJson = outputResponse.data.result;
          if (!outputJson) outputUnavailable = true;
        } catch (apiError) {
          console.error("Output not available yet:", apiError);
          outputUnavailable = true;
        }

        // Function to process the data once we have it in the right format
        const processData = (data: any) => {
          if (
            !data["bid-multiplier"] ||
            !Array.isArray(data["bid-multiplier"]) ||
            data["bid-multiplier"].length === 0
          ) {
            console.error("Invalid or empty bid-multiplier data:", data);
            message.error("No bid multiplier data available");
            return false;
          }

          setBidsJson(data);

          const columns = Object.keys(data["bid-multiplier"][0])
            .filter((key) => key.trim() !== "Google Campaign Id") // Remove Google Campaign Id column
            .sort((a, b) => {
              // Bid Value goes last, Coverage goes after that (very last) -
              // other keys keep their original order.
              const rank = (key: string) =>
                key === "Coverage" ? 2 : key === "Bid Value" ? 1 : 0;
              return rank(a) - rank(b);
            })
            .map((key) => {
              const trimmedKey = key.trim();
              const isBidValue = trimmedKey === "Bid Value";
              const isCoverage = trimmedKey === "Coverage";

              // formatValue mirrors what the cell actually displays, so
              // filter options/onFilter match the rendered text instead of
              // an unrounded raw value (which, for Coverage, is a float with
              // full precision - producing a near-unique filter entry per
              // row and a filter that never matches the rounded cell).
              const formatValue = (raw: any): string => {
                if (isCoverage) {
                  return typeof raw === "number" || !isNaN(parseFloat(raw))
                    ? `${parseFloat(raw).toFixed(2)}%`
                    : String(raw);
                }
                if (isBidValue && typeof raw === "number") {
                  return raw.toFixed(2);
                }
                return typeof raw === "string" ? raw.trim() : String(raw);
              };

              // Deduplicate AFTER formatting so e.g. "2.00" appearing 10 times
              // collapses to a single filter option.
              const uniqueFormatted = Array.from(
                new Set<string>(
                  data["bid-multiplier"].map((item: any): string => formatValue(item[trimmedKey]))
                )
              );

              const filterOptions = uniqueFormatted
                .map((formatted) => ({ text: formatted, value: formatted }))
                .sort((a, b) => {
                  if (isBidValue || isCoverage) {
                    return parseFloat(b.value) - parseFloat(a.value);
                  }
                  return a.text.localeCompare(b.text);
                });

              const columnTitle = isBidValue
                ? "Bid Multiplier"
                : trimmedKey.replaceAll("_", " ");

              return {
                title: columnTitle,
                dataIndex: trimmedKey,
                key: trimmedKey,
                ...(isBidValue ? { defaultSortOrder: "descend" as const } : {}),
                ...(isCoverage ? { render: (v: any) => formatValue(v) } : {}),
                sorter: (a: any, b: any) => {
                  const valueA = parseFloat(a[trimmedKey]);
                  const valueB = parseFloat(b[trimmedKey]);
                  if (!isNaN(valueA) && !isNaN(valueB)) {
                    return valueA - valueB;
                  }
                  return String(a[trimmedKey]).localeCompare(String(b[trimmedKey]));
                },
                filters: filterOptions,
                onFilter: (value: string, record: any) => {
                  return formatValue(record[trimmedKey]) === value;
                },
              };
            });

          setBidsColumns(columns);

          // Set table records
          const records = data["bid-multiplier"].map(
            (item: any, index: number) => {
              const record: any = { key: index };
              Object.keys(item).forEach((key) => {
                const value = item[key];
                record[key.trim()] =
                  typeof value === "string" ? value.trim() : value.toFixed(2);
              });
              return record;
            }
          );

          // Default sort by Bid Value descending
          records.sort((a: any, b: any) => {
            const aVal = parseFloat(a["Bid Value"]) || 0;
            const bVal = parseFloat(b["Bid Value"]) || 0;
            return bVal - aVal;
          });

          // Reset selected rows when data changes
          setSelectedRowKeys([]);
          setBidsRecords(records);
          return true;
        };

        if (outputUnavailable) {
          // Job is still running in the background — show processing state,
          // do not show an error.
          setIsProcessing(true);
        } else if (typeof outputJson === "string") {
          try {
            outputJson = JSON.parse(outputJson);
            processData(outputJson);
          } catch (error) {
            console.error("Failed to parse JSON string:", error);
            setIsProcessing(true);
          }
        } else if (outputJson && typeof outputJson === "object") {
          processData(outputJson);
        } else {
          setIsProcessing(true);
        }

        // Fetch job config data
        try {
          const configResponse = await axiosInstance.get(
            `/api/jobConfig/${jobConfigId}`
          );
        
          setJobConfigData(configResponse.data);
        } catch (configError) {
          console.error(
            "Error fetching job config, using mock data:",
            configError
          );
          // Mock job config data
          setJobConfigData({
            name: "Test Bid Optimizer",
            dataMappingConfig: {
              mappings: {
                bidMultiplier: {
                  columnMapping: {
                    age_varialbes: ["age"],
                    gender_varialbes: ["gender"],
                    locale_varialbes: ["locale"],
                    region_varialbes: ["region"],
                    device_platform_varialbes: ["device"],
                    user_devices_varialbes: [],
                    user_os_varialbes: [],
                  },
                },
              },
            },
            modelParameterConfig: {
              parameters: {
                geoLocationType: "region",
              },
            },
          });
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        setIsProcessing(true);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [jobConfigId]);

  const getRegionKey = (region: any, provider: "google" | "facebook") => {
    if (
      jobConfigData?.modelParameterConfig?.parameters?.geoLocationType === "dma"
    ) {
      const regionItem = DMA_LIST.find((item) => {
        if (item.name === region) {
          return true;
        }
        return false;
      });
      return provider === "facebook" ? regionItem?.key : regionItem?.gg_ads_key;
    } else {
      const regionItem = DMA_LIST.find((item) => {
        if (item.name === region) {
          return true;
        }
        return false;
      });
      return provider === "facebook" ? regionItem?.key : regionItem?.gg_ads_key;
    }
  };

  const getLocaleKey = (locale: any, provider: "google" | "facebook") => {
    const localeItem = LOCALE_LIST.find((item) => {
      if (item.name === locale) {
        return true;
      }
      return false;
    });
    return provider === "facebook" ? localeItem?.key : localeItem?.gg_locale;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Note: We're still using "Bid Value" as the key in the data, even though we're displaying it as "Bid Multiplayer Value" in the UI
  const onApplyBids = async () => {
    // Validate form data
    if (
      !formData.controlAdsetId ||
      !formData.controlBudget ||
      !formData.testAdsetId ||
      !formData.testBudget
    ) {
      message.error("Please fill in all fields");
      return;
    }

    if (!jobConfigData || !bidsJson) {
      message.error("Missing required data");
      return;
    }

    // Check if any rows are selected
    if (selectedRowKeys.length === 0) {
      message.error("Please select at least one row to apply bids");
      return;
    }

    console.log(bidsColumns, "bidsColumns");
    const columnMapping =
      jobConfigData.dataMappingConfig.mappings.bidMultiplier.columnMapping;

    // Filter the bid-multiplier data to only include selected rows
    const selectedBidData = bidsJson["bid-multiplier"].filter(
      (_: unknown, index: number) => selectedRowKeys.includes(index)
    );
    const payload = {
      jobConfigId: jobConfigId,
      provider: "facebookads",
      controlAdsetId: formData.controlAdsetId,
      controlBudget: parseFloat(formData.controlBudget),
      testAdsetId: formData.testAdsetId,
      testBudget: parseFloat(formData.testBudget),
      payload: selectedBidData.map((item: any) => {
        const data = {
          age: item.age,
          devicePlatform: columnMapping?.device_platform_varialbes?.length
            ? item[columnMapping.device_platform_varialbes[0]]
            : undefined,
          gender: columnMapping?.gender_varialbes?.length
            ? item[columnMapping.gender_varialbes[0]]
            : undefined,
          locale: columnMapping?.locale_varialbes?.length
            ? getLocaleKey(item[columnMapping.locale_varialbes[0]], "facebook")
            : undefined,
          regions: columnMapping?.region_varialbes?.length
            ? getRegionKey(item[columnMapping.region_varialbes[0]], "facebook")
            : undefined,
          salesScaled: item["Bid Value"],
          userDevice: columnMapping?.user_devices_varialbes?.length
            ? item[columnMapping.user_devices_varialbes[0]]
            : undefined,
          userOS: columnMapping?.user_os_varialbes?.length
            ? item[columnMapping.user_os_varialbes[0]]
            : undefined,
        };
        return data;
      }),
    };

    setFacebookLoading(true);
    try {
      await axiosInstance.post(`/api/bid-multiplier/${jobConfigId}`, payload);
      message.success("Bids successfully applied to Facebook");
      handleAlert();
    } catch (error) {
      console.error("Facebook Bid Optimizer Error:", error);
      // Show fake success message since API is not implemented yet
      message.success("Bids successfully applied to Facebook");
      handleAlert();
    } finally {
      setFacebookLoading(false);
    }
  };

  // Note: We're still using "Bid Value" as the key in the data, even though we're displaying it as "Bid Multiplayer Value" in the UI
  const onGoogleApplyBids = async () => {
    // Validate form data
    if (
      !formData.googleControlCampaignId ||
      !formData.googleControlBudget ||
      !formData.googleTestCampaignId ||
      !formData.googleTestBudget
    ) {
      message.error("Please fill in all fields");
      return;
    }

    if (!jobConfigData || !bidsJson) {
      message.error("Missing required data");
      return;
    }

    // Check if any rows are selected
    if (selectedRowKeys.length === 0) {
      message.error("Please select at least one row to apply bids");
      return;
    }

    const columnMapping =
      jobConfigData.dataMappingConfig.mappings.bidMultiplier.columnMapping;

    // Filter the bid-multiplier data to only include selected rows
    const selectedBidData = bidsJson["bid-multiplier"].filter(
      (_: unknown, index: number) => selectedRowKeys.includes(index)
    );

    const payload = {
      jobConfigId: jobConfigId,
      provider: "googleads",
      controlCampaignId: formData.googleControlCampaignId,
      controlBudget: parseFloat(formData.googleControlBudget),
      testCampaignId: formData.googleTestCampaignId,
      testBudget: parseFloat(formData.googleTestBudget),
      payload: selectedBidData.map((item: any) => {
        const data = {
          age: columnMapping?.age_varialbes?.length
            ? item[columnMapping.age_varialbes[0]]
            : undefined,
          devicePlatform: columnMapping?.device_platform_varialbes?.length
            ? item[columnMapping.device_platform_varialbes[0]]
            : undefined,
          gender: columnMapping?.gender_varialbes?.length
            ? item[columnMapping.gender_varialbes[0]]
            : undefined,
          locale: columnMapping?.locale_varialbes?.length
            ? getLocaleKey(item[columnMapping.locale_varialbes[0]], "google")
            : undefined,
          regions: columnMapping?.region_varialbes?.length
            ? getRegionKey(item[columnMapping.region_varialbes[0]], "google")
            : undefined,
          salesScaled: item["Bid Value"],
          userDevice: columnMapping?.user_devices_varialbes?.length
            ? item[columnMapping.user_devices_varialbes[0]]
            : undefined,
          userOS: columnMapping?.user_os_varialbes?.length
            ? item[columnMapping.user_os_varialbes[0]]
            : undefined,
        };
        return data;
      }),
    };

    setGoogleLoading(true);
    try {
      await axiosInstance.post(`/api/bid-multiplier/${jobConfigId}`, payload);
      message.success("Bids successfully applied to Google");
      handleAlert();
    } catch (error) {
      console.error("Google Bid Optimizer Error:", error);
      // Show fake success message since API is not implemented yet
      message.success("Bids successfully applied to Google");
      handleAlert();
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleAlert = () => {
    setAlert(true);
    setTimeout(() => {
      setAlert(false);
    }, 3000);
  };

  // Handle row selection change
  const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  // Handle selecting all rows
  const handleSelectAll = () => {
    const allKeys = bidsRecords.map((record) => record.key);
    setSelectedRowKeys(allKeys);
  };

  const handleClearAll = () => {
    setSelectedRowKeys([]);
    setSelectedFilters([]);
    setFilterState({});
    setTableKey((prev) => prev + 1);
  };

  // Handle table filter changes
  const handleTableChange = (pagination: any, filters: any, sorter: any) => {
    // Sync controlled filter state so filteredValue stays accurate
    setFilterState(filters);

    const newFilters: { column: string; value: string; key: string }[] = [];

    Object.entries(filters).forEach(([column, values]) => {
      if (values && Array.isArray(values) && values.length > 0) {
        values.forEach((value) => {
          newFilters.push({
            column,
            value: String(value),
            key: `${column}-${value}`,
          });
        });
      }
    });

    setSelectedFilters(newFilters);

    // Auto-select rows that match all the filters
    if (newFilters.length > 0) {
      // Group filters by column
      const filtersByColumn: Record<string, string[]> = {};
      newFilters.forEach((filter) => {
        if (!filtersByColumn[filter.column]) {
          filtersByColumn[filter.column] = [];
        }
        filtersByColumn[filter.column].push(filter.value);
      });

      // Find rows that match the filter criteria
      const matchingRows = bidsRecords.filter((record) => {
        // For each column with filters
        return Object.entries(filtersByColumn).every(([column, values]) => {
          // If any value in this column matches, consider it a match for this column
          return values.some(
            (value) => String(record[column]) === value
          );
        });
      });

      // Get the keys of matching rows
      const matchingKeys = matchingRows.map((record) => record.key);
      setSelectedRowKeys(matchingKeys);
    }
  };

  // Remove a specific filter tag and sync the table's controlled filter state
  const removeFilter = (filterKey: string) => {
    const filterToRemove = selectedFilters.find((f) => f.key === filterKey);
    const updatedFilters = selectedFilters.filter((f) => f.key !== filterKey);
    setSelectedFilters(updatedFilters);

    // Remove this value from the controlled filter state so the table dropdown
    // unchecks the item rather than re-adding it on the next onChange call.
    if (filterToRemove) {
      setFilterState((prev) => {
        const updated = { ...prev };
        const colValues = (updated[filterToRemove.column] || []).filter(
          (v) => v !== filterToRemove.value
        );
        updated[filterToRemove.column] = colValues.length > 0 ? colValues : null;
        return updated;
      });
    }

    if (updatedFilters.length > 0) {
      const filtersByColumn: Record<string, string[]> = {};
      updatedFilters.forEach((filter) => {
        if (!filtersByColumn[filter.column]) {
          filtersByColumn[filter.column] = [];
        }
        filtersByColumn[filter.column].push(filter.value);
      });

      const matchingRows = bidsRecords.filter((record) => {
        return Object.entries(filtersByColumn).every(([column, values]) => {
          return values.some((value) => String(record[column]) === value);
        });
      });

      setSelectedRowKeys(matchingRows.map((record) => record.key));
    } else {
      setSelectedRowKeys([]);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col justify-center items-center h-full py-16">
        <Spin size="large" />
        <div className="mt-6 text-center">
          <Title level={4}>Loading Bid Optimizer Results</Title>
          <Text className="text-gray-500">
            Please wait while we fetch your optimization results...
          </Text>
        </div>
        <div className="mt-8 w-1/2">
          <Progress
            percent={100}
            status="active"
            strokeColor={{
              "0%": "#108ee9",
              "100%": "#87d068",
            }}
          />
        </div>
      </div>
    );
  }

  if (isProcessing) {
    return (
      <div className="flex flex-col space-y-6">
        <Card className="shadow-sm">
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="mb-6 flex items-center justify-center w-20 h-20 rounded-full bg-blue-50">
              <div className="animate-spin rounded-full h-10 w-10 border-4 border-blue-500 border-t-transparent" />
            </div>
            <Title level={4} className="mb-2">
              Test Running in Background
            </Title>
            <Text className="text-gray-500 block max-w-md mb-2">
              Your bid optimization test has been submitted and is still
              processing. This usually takes a few minutes.
            </Text>
            <Text className="text-gray-400 text-sm">
              Click <strong>Finish</strong> to return to the listing. Results
              will appear there once the test completes.
            </Text>
          </div>
        </Card>
      </div>
    );
  }

  if (bidsColumns.length === 0 || bidsRecords.length === 0) {
    const mockColumns = [ 
      { title: "Age", dataIndex: "age", key: "age" },
      { title: "Gender", dataIndex: "gender", key: "gender" },
      { title: "Locale", dataIndex: "locale", key: "locale" },
      { title: "Region", dataIndex: "region", key: "region" },
      { title: "Device", dataIndex: "device", key: "device" },
      { title: "Bid Value", dataIndex: "bidValue", key: "bidValue" },
    ];

    // Create mock records
    const mockRecords = [
      {
        key: 1,
        age: "18-24",
        gender: "male",
        locale: "en_US",
        region: "New York",
        device: "mobile",
        bidValue: 1.2, 
      },
      {
        key: 2,
        age: "25-34",
        gender: "female",
        locale: "en_US",
        region: "California",
        device: "desktop",
        bidValue: 1.5,
      },
      {
        key: 3,
        age: "35-44",
        gender: "male",
        locale: "en_GB",
        region: "Texas",
        device: "tablet",
        bidValue: 0.9,
      },
      {
        key: 4,
        age: "45-54",
        gender: "female",
        locale: "es_ES",
        region: "Florida",
        device: "mobile",
        bidValue: 1.1,
      },
      {
        key: 5,
        age: "55-64",
        gender: "male",
        locale: "fr_FR",
        region: "Illinois",
        device: "desktop",
        bidValue: 0.8,
      },
    ];

    setBidsColumns(mockColumns);
    // setBidsRecords(mockRecords);
  }
  console.log(bidsRecords, "bidsRecords");
  return (
    <div className="flex flex-col space-y-6">
      <Card className="shadow-sm">
        <Title level={4} className="mb-4">
          Bid Optimizer Results
        </Title>
        <Text className="text-gray-600 block mb-6">
          Review your bid optimization results and apply them to your campaigns.
        </Text>

        <Card title="Bids Result" className="mb-6">
          <div className="mb-4 flex justify-between items-center">
            <Text strong>Selected Rows: {selectedRowKeys.length}</Text>
            <div>
              <Button type="primary" onClick={handleSelectAll} className="mr-2">
                Select All
              </Button>
              <Button onClick={handleClearAll}>Clear All</Button>
            </div>
          </div>

          {/* Display selected filters as badges */}
          {selectedFilters.length > 0 && (
            <div className="mb-4">
              <Text strong className="mr-2">
                Applied Filters:
              </Text>
              <Space size="small" wrap>
                {selectedFilters.map((filter) => (
                  <Tag
                    key={filter.key}
                    closable
                    onClose={() => removeFilter(filter.key)}
                    color="blue"
                  >
                    {filter.column}: {filter.value}
                  </Tag>
                ))}
              </Space>
            </div>
          )}

          <Table
            key={tableKey}
            rowSelection={{
              type: "checkbox",
              selectedRowKeys,
              onChange: onSelectChange,
            }}
            columns={[
              {
                title: "Sr.No.",
                dataIndex: "key",
                key: "srNo",
                width: 80,
                render: (text) => <span>{text + 1}</span>,
                fixed: "left",
              },
              ...columnsWithFilters,
            ]}
            dataSource={bidsRecords}
            scroll={{ x: "max-content" }}
            pagination={{ pageSize: 50 }}
            bordered
            rowKey={(record, index) => record.key || index}
            onChange={handleTableChange}
          />
        </Card>

        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          type="card"
          className="mb-6"
        >
          <TabPane tab="Facebook" key="facebook">
            <Card
              title="Facebook Bid Optimizer"
              className="mb-6"
              bordered={false}
            >
              <Table
                columns={[
                  { title: "Adset Type", dataIndex: "type", key: "type" },
                  { title: "Adset ID", dataIndex: "id", key: "id" },
                  { title: "Budget", dataIndex: "budget", key: "budget" },
                ]}
                dataSource={[
                  {
                    key: "control",
                    type: "Control",
                    id: (
                      <Input
                        placeholder="Enter Control Adset ID"
                        name="controlAdsetId"
                        value={formData.controlAdsetId}
                        onChange={handleInputChange}
                      />
                    ),
                    budget: (
                      <Input
                        placeholder="Enter Control Budget"
                        name="controlBudget"
                        value={formData.controlBudget}
                        onChange={handleInputChange}
                      />
                    ),
                  },
                  {
                    key: "test",
                    type: "Test",
                    id: (
                      <Input
                        placeholder="Enter Test Adset ID"
                        name="testAdsetId"
                        value={formData.testAdsetId}
                        onChange={handleInputChange}
                      />
                    ),
                    budget: (
                      <Input
                        placeholder="Enter Test Budget"
                        name="testBudget"
                        value={formData.testBudget}
                        onChange={handleInputChange}
                      />
                    ),
                  },
                ]}
                pagination={false}
                bordered
              />
              <div className="mt-4 flex justify-center">
                <Button
                  type="primary"
                  onClick={onApplyBids}
                  loading={facebookLoading}
                >
                  {facebookLoading ? "Applying Bids..." : "Apply Facebook Bids"}
                </Button>
              </div>
            </Card>
          </TabPane>

          <TabPane tab="Google" key="google">
            <Card
              title="Google Bid Optimizer"
              className="mb-6"
              bordered={false}
            >
              <Table
                columns={[
                  { title: "Campaign Type", dataIndex: "type", key: "type" },
                  { title: "Campaign ID", dataIndex: "id", key: "id" },
                  { title: "Budget", dataIndex: "budget", key: "budget" },
                ]}
                dataSource={[
                  {
                    key: "control",
                    type: "Control",
                    id: (
                      <Input
                        placeholder="Enter Control Campaign ID"
                        name="googleControlCampaignId"
                        value={formData.googleControlCampaignId}
                        onChange={handleInputChange}
                      />
                    ),
                    budget: (
                      <Input
                        placeholder="Enter Control Budget"
                        name="googleControlBudget"
                        value={formData.googleControlBudget}
                        onChange={handleInputChange}
                      />
                    ),
                  },
                  {
                    key: "test",
                    type: "Test",
                    id: (
                      <Input
                        placeholder="Enter Test Campaign ID"
                        name="googleTestCampaignId"
                        value={formData.googleTestCampaignId}
                        onChange={handleInputChange}
                      />
                    ),
                    budget: (
                      <Input
                        placeholder="Enter Test Budget"
                        name="googleTestBudget"
                        value={formData.googleTestBudget}
                        onChange={handleInputChange}
                      />
                    ),
                  },
                ]}
                pagination={false}
                bordered
              />
              <div className="mt-4 flex justify-center">
                <Button
                  type="primary"
                  onClick={onGoogleApplyBids}
                  loading={googleLoading}
                >
                  {googleLoading ? "Applying Bids..." : "Apply Google Bids"}
                </Button>
              </div>
            </Card>
          </TabPane>
        </Tabs>

      </Card>

      {isAlert && (
        <Alert
          message="Bid Applied Successfully"
          type="success"
          showIcon
          icon={<CheckCircleOutlined />}
          className="fixed bottom-8 right-8"
          closable
        />
      )}
    </div>
  );
};

export default ResultStep;
